import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { FormGroup, Button, Intent, Spinner } from "@blueprintjs/core";
import { observable } from 'mobx';
import Toast from 'helpers/toaster';
import FormDrawer from 'components/FormDrawer';
import { FormSection, FieldError } from 'components/FormBits';

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

// Bring a field with an error into view and put the cursor in it.
const focusField = (id) => {
  setTimeout(() => {
    const field = document.getElementById(id);
    if (!field) return;
    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    field.focus({ preventScroll: true });
  }, 0);
};

@inject('paymentMethodStore', 'quickbooksStore') @observer
class PaymentMethodModal extends Component {
  @observable errors = {};

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.errors = {};
    }
  }

  clearError(key) {
    if (this.errors[key]) {
      this.errors = { ...this.errors, [key]: null };
    }
  }

  // Inline errors for the required fields; focus the first one.
  validate() {
    const { edit, paymentMethodStore, quickbooksStore } = this.props;
    const object = paymentMethodStore.paymentMethod.object;
    const options = quickbooksStore.quickbooksPaymentMethods.objects;
    const errors = {};
    if (!edit && isBlank(object.name)) {
      errors.name = 'Enter a name, e.g. Credit card.';
    }
    if (options && options.length > 0 && isBlank(object.quickbooks_payment_method_id)) {
      errors.quickbooks = 'Choose the QuickBooks payment method this matches.';
    }
    this.errors = errors;
    if (errors.name) focusField('payment-method-name');
    else if (errors.quickbooks) focusField('quickbooks-payment-method');
    return !errors.name && !errors.quickbooks;
  }

  handleCloseDailog() {
    // clearing object
    this.props.paymentMethodStore.paymentMethod.object = {}
    this.props.onClose()
  }

  updatePaymentMethod() {
    let { paymentMethodStore } = this.props;
    if (!this.validate()) return;
    paymentMethodStore.update()
      .then(this.onSuccessfullyUpdatePaymentMethod.bind(this))
      .catch(this.onFailCreatePaymentMethod.bind(this))
  }

  onSuccessfullyUpdatePaymentMethod(response) {
    this.props.onSuccess()
    Toast.show({ message: "Payment method updated successfully.", intent: Intent.SUCCESS })
  }

  createPaymentMethod() {
    let { paymentMethodStore } = this.props;
    if (!this.validate()) return;
    paymentMethodStore.create()
      .then(this.onSuccessfullyCreatePaymentMethod.bind(this))
      .catch(this.onFailCreatePaymentMethod.bind(this))
  }

  createPaymentMethodOnQuickbooks(){
    let { 
      paymentMethodStore,
      paymentMethodStore: {
        paymentMethod:{
          object
        }
      }
     } = this.props;
    object.payment_method_name = 'Credit Card (FieldNexus)';
    paymentMethodStore.createOnQuickBooks()
      .then(this.onSuccessfullyCreatePaymentMethodOnQuickbooks.bind(this))
      .catch(this.onFailCreatePaymentMethod.bind(this))

  }

  onSuccessfullyCreatePaymentMethodOnQuickbooks(response) {
    this.props.quickbooksStore.loadQuickbooksPaymentMethods();
    Toast.show({ message: "Payment method created successfully.", intent: Intent.SUCCESS })
  }

  onSuccessfullyCreatePaymentMethod(response) {
    this.props.onSuccess()
    Toast.show({ message: "Payment method created successfully.", intent: Intent.SUCCESS })
  }

  onFailCreatePaymentMethod(error) {
    if(error.response && error.response.data && error.response.data.errors) {
      Toast.show({ message: error.response.data.errors, intent: Intent.DANGER })
    } else {
      Toast.show({ message: "Something went wrong. Failed to create/update payment method", intent: Intent.DANGER })
    }
  }

  render() {
    let {
      edit,
      paymentMethodStore: {
        paymentMethod: {
          isLoading,
          object
        }
      },
      quickbooksStore: { 
        quickbooksPaymentMethods: {
          objects
        }
      }
    } = this.props;
    let credit_card_fieldnexus = ';'

    if(objects && objects.length > 0){
      credit_card_fieldnexus = objects.map(function(item) { return item.name; }).indexOf('Credit Card (FieldNexus)');
    }
    const busy = !!isLoading;
    const close = this.handleCloseDailog.bind(this);
    const errors = this.errors;
    const quickbooksLoading = this.props.quickbooksStore.quickbooksPaymentMethods.isLoading;

    return(
      <FormDrawer
        title={edit ? "Edit payment method" : "New payment method"}
        icon={edit ? "edit" : "credit-card"}
        isOpen={this.props.isOpen}
        onClose={close}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={close} disabled={busy} />
            {edit ?
              <Button
                intent={Intent.PRIMARY}
                icon="floppy-disk"
                text="Save changes"
                loading={busy}
                onClick={this.updatePaymentMethod.bind(this)}
              />
              :
              <Button
                intent={Intent.PRIMARY}
                icon="add"
                text="Create payment method"
                loading={busy}
                onClick={this.createPaymentMethod.bind(this)}
              />
            }
          </>
        }
      >
        <FormSection title="Payment method" hint="How customers pay, as it shows on payments you record.">
          <FormGroup
            className={errors.name ? 'has-error' : undefined}
            label="Name"
            labelFor="payment-method-name"
            labelInfo="*"
            helperText={edit ? 'The name can’t be changed.' : undefined}
          >
            <input
              id="payment-method-name"
              type='text'
              className='bp3-input bp3-fill'
              placeholder='e.g. Credit card'
              readOnly={edit}
              defaultValue={object.name}
              aria-invalid={!!errors.name}
              onChange={(event) => { object.name = event.target.value; this.clearError('name'); } }
            />
            <FieldError>{errors.name}</FieldError>
          </FormGroup>
        </FormSection>

        <FormSection title="QuickBooks" hint="The matching payment method in QuickBooks, so payments sync to it.">
          <FormGroup
            className={errors.quickbooks ? 'has-error' : undefined}
            label="QuickBooks payment method"
            labelFor="quickbooks-payment-method"
            labelInfo="*"
          >
            {quickbooksLoading && <Spinner size={Spinner.SIZE_SMALL} />}
            {!quickbooksLoading && <div className="bp3-select bp3-fill">
              <select
                id="quickbooks-payment-method"
                value={object.quickbooks_payment_method_id || ''}
                aria-invalid={!!errors.quickbooks}
                onChange={event => {
                  object.quickbooks_payment_method_id = event.target.value;
                  this.clearError('quickbooks');
                }}
              >
                <option value="">Choose a QuickBooks payment method</option>
                {objects && objects.length > 0 && objects.map((paymentMethod) => {
                  return(
                    <option value={paymentMethod.id} key={paymentMethod.id}>{paymentMethod.name}</option>
                  )
                })}
              </select>
            </div>}
            <FieldError>{errors.quickbooks}</FieldError>
          </FormGroup>
          { credit_card_fieldnexus === -1 && !edit &&
            <FormGroup helperText="Adds a “Credit Card (FieldNexus)” payment method to QuickBooks.">
              <Button icon="add" small={true} loading={busy} onClick={this.createPaymentMethodOnQuickbooks.bind(this)}>
                Add to QuickBooks
              </Button>
            </FormGroup>
          }
        </FormSection>
      </FormDrawer>
    )
  }
};

export default PaymentMethodModal;
